import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Button,
  Tooltip,
  styled,
  SelectChangeEvent,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import { fetchRoles, fetchRolesCount, createRole } from '../api/features/roles/rolesApi';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { SAPIconButton, PageLayout, FilterBar } from '../components';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY, SAP_SHADOWS } from '../theme/sapDesignSystem';

const ROWS_PER_PAGE = 10;

// SAP Fiori Roles Components - matching other pages exactly

const SAPFilterCard = styled(Card)(({ theme }) => ({
  marginBottom: SAP_SPACING.lg,
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
}));

const SAPDataCard = styled(Card)(({ theme }) => ({
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
}));

const SAPTableHeader = styled(TableHead)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapGray1,
  '& .MuiTableCell-head': {
    fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
    color: SAP_COLORS.sapGray6,
    borderBottom: `2px solid ${SAP_COLORS.sapBlue}`,
  },
}));

export default function Roles() {
  const navigate = useNavigate();
  const { clientCode } = useClientCode();

  // State
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState<number>(0);
  const [searchName, setSearchName] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [rolesData, countData] = await Promise.all([
        fetchRoles({
          clientCode,
          firstResult: page * ROWS_PER_PAGE,
          maxResult: ROWS_PER_PAGE,
          name: searchName || undefined,
          searched: true,
        }),
        fetchRolesCount({
          clientCode,
          name: searchName || undefined,
          searched: true,
        }),
      ]);

      setRoles(rolesData);
      setRowCount(countData || 0);
    } catch (err) {
      logger.error('Error fetching roles:', err);
      setError('Failed to fetch roles');
      setRoles([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    setHasSearched(true);
    fetchData();
  };

  const handleReset = () => {
    setSearchName('');
    setPage(0);
    setHasSearched(false);
    setRoles([]);
    setRowCount(0);
    setError(null);
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) {
      toast.error('Please fill in role name');
      return;
    }

    try {
      await createRole({
        clientCode,
        payload: { roleName: newRoleName.trim() },
      });
      toast.success('Role created successfully!');
      setCreateDialogOpen(false);
      setNewRoleName('');
      fetchData();
      setError(null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create role');
    }
  };

  const handleEditClick = (role: any) => {
    navigate(`/roleEdit/${role.id}`, { 
      state: { roleName: role.name, roleId: role.id }
    });
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
    // Don't call fetchData here, let the useEffect handle it
  };

  // Fetch data when page changes
  useEffect(() => {
    if (hasSearched) {
      fetchData();
    }
  }, [page]);

  return (
    <PageLayout
      title="Role Management"
      description="Search and manage roles for your organization"
      titleSx={{ color: SAP_COLORS.sapBlue }}
    >
      <FilterBar loading={loading}>
        <TextField
          label="Role Name"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          placeholder="Enter role name"
          size="small"
          fullWidth
          sx={{ flex: 1 }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <SAPIconButton
            icon="search"
            variant="primary"
            onClick={handleSearch}
            disabled={loading}
            aria-label="Search"
          />
          <SAPIconButton
            icon="reset"
            variant="secondary"
            onClick={handleReset}
            disabled={loading}
            aria-label="Reset"
          />
          <SAPIconButton
            icon="add"
            variant="success"
            onClick={() => setCreateDialogOpen(true)}
            disabled={loading}
            aria-label="Create Role"
          />
        </Box>
      </FilterBar>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
          {error}
        </Alert>
      )}

      {/* Data Card */}
      <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <CircularProgress sx={{ color: SAP_COLORS.sapBlue }} size={48} />
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <Alert severity="error" sx={{ 
                borderRadius: '8px',
                backgroundColor: SAP_COLORS.sapRed + '10',
                color: SAP_COLORS.sapRed,
                border: `1px solid ${SAP_COLORS.sapRed}30`,
              }}>
                {error}
              </Alert>
            </Box>
          ) : !hasSearched ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <Typography variant="body1" sx={{ color: SAP_COLORS.sapGray5 }}>
                Please use the search filters above to view roles.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  {/* SAP Fiori Table Header */}
                  <SAPTableHeader>
                    <TableRow>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                        width: '80px'
                      }}>
                        S.No.
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        Role Name
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                        width: '100px'
                      }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </SAPTableHeader>
                  
                  {/* Table Body */}
                  <TableBody>
                    {roles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ 
                          color: SAP_COLORS.sapGray5,
                          py: SAP_SPACING.xl,
                          fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                        }}>
                          No roles found. Try searching with different criteria.
                        </TableCell>
                      </TableRow>
                    ) : (
                      roles.map((role, idx) => (
                        <TableRow
                          key={role.id}
                          sx={{
                            bgcolor: idx % 2 === 0 ? SAP_COLORS.sapWhite : SAP_COLORS.sapGray1,
                            '&:hover': { 
                              bgcolor: SAP_COLORS.hover,
                            },
                            transition: 'background-color 0.2s ease',
                          }}
                        >
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray6, 
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium 
                          }}>
                            {page * ROWS_PER_PAGE + idx + 1}
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray7,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                          }}>
                            {role.name || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Tooltip title="Edit Role" arrow>
                                <IconButton 
                                  size="small"
                                  onClick={() => handleEditClick(role)}
                                  sx={{ 
                                    color: SAP_COLORS.sapBlue,
                                    '&:hover': { 
                                      backgroundColor: SAP_COLORS.hover,
                                    },
                                  }}
                                  aria-label="Edit Role"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              {roles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {/* Pagination Info */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {(page * ROWS_PER_PAGE) + 1}-{Math.min((page * ROWS_PER_PAGE) + roles.length, rowCount)} of {rowCount} roles
                    </Typography>
                  </Box>
                  
                  {/* Pagination Controls */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Rows per page</InputLabel>
                      <Select
                        value={ROWS_PER_PAGE}
                        onChange={(event: SelectChangeEvent<number>) => {
                          // Note: ROWS_PER_PAGE is constant, so this is just for UI consistency
                        }}
                        label="Rows per page"
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Pagination
                      count={Math.ceil(rowCount / ROWS_PER_PAGE)}
                      page={page + 1}
                      onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                        handlePageChange(event, newPage - 1);
                      }}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Role Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="body"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            border: `1px solid ${SAP_COLORS.sapGray2}`,
            boxShadow: SAP_SHADOWS.lg,
            maxWidth: { xs: 'calc(100% - 32px)', sm: 480 },
            minWidth: { xs: 0, sm: 400 },
            m: { xs: 2, sm: 3 },
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: SAP_COLORS.sapGray1,
            color: SAP_COLORS.sapGray7,
            fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
            fontSize: { xs: '1.125rem', sm: '1.25rem' },
            py: { xs: 2, sm: 2.5 },
            px: { xs: 3, sm: 4 },
            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
          }}
        >
          Create New Role
        </DialogTitle>
        <DialogContent
          sx={{
            bgcolor: SAP_COLORS.sapWhite,
            py: { xs: 3, sm: 4 },
            px: { xs: 3, sm: 4 },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Role Name"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              placeholder="Enter role name"
              fullWidth
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: SAP_COLORS.sapWhite,
                  fontSize: { xs: '1rem', sm: '1.0625rem' },
                  minHeight: { xs: 48, sm: 56 },
                  '& fieldset': { borderColor: SAP_COLORS.sapGray3 },
                  '&:hover fieldset': { borderColor: SAP_COLORS.sapBlue },
                  '&.Mui-focused fieldset': { borderColor: SAP_COLORS.sapBlue, borderWidth: '2px' },
                },
                '& .MuiInputLabel-root': {
                  color: SAP_COLORS.sapGray5,
                  fontSize: { xs: '0.9375rem', sm: '1rem' },
                },
                '& .MuiInputBase-input': {
                  color: SAP_COLORS.sapGray7,
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            bgcolor: SAP_COLORS.sapGray1,
            py: { xs: 2, sm: 2.5 },
            px: { xs: 3, sm: 4 },
            gap: 1,
            borderTop: `1px solid ${SAP_COLORS.sapGray2}`,
          }}
        >
          <Button
            onClick={() => setCreateDialogOpen(false)}
            size="medium"
            sx={{
              color: SAP_COLORS.sapGray6,
              fontSize: { xs: '0.9375rem', sm: '1rem' },
              px: { xs: 2, sm: 3 },
              '&:hover': { backgroundColor: SAP_COLORS.hover },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateRole}
            variant="contained"
            size="medium"
            sx={{
              bgcolor: SAP_COLORS.sapGreen,
              '&:hover': { bgcolor: SAP_COLORS.sapGreen + 'dd' },
              color: SAP_COLORS.sapWhite,
              fontSize: { xs: '0.9375rem', sm: '1rem' },
              px: { xs: 2.5, sm: 3 },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

    </PageLayout>
  );
} 